import { useEffect, useRef, useState, useCallback } from 'react';
import './ConsensusRecommendationWidget.css';
import useResizeNotifier from '../../hooks/useResizeNotifier';
import useContainerWidth from '../../hooks/useContainerWidth';
import {
  ConsensusRecommendationCount,
  RecommendationData,
  RecommendationDataType,
} from '../../types/company.type';
import Header from '../../Components/Common/Header/Header';
import Dropdown from '../../Components/Common/Dropdown/Dropdown';
import { countryMenu, recommendationInitalMenu } from '../../utils/constant';
import RecommendationTable from '../../Components/RecommendationTable/RecommendationTable';
import Pagination from '../../Components/Common/Pagination/Pagination';
import {
  paginate,
  PaginationType,
  getMaxPageNum,
} from '../../utils/pagination';
import { getRecommendationData } from '../../api/company.api';
import {
  Region,
  ThemeType,
  Type,
  WidgetMessageType,
  WidgetNameSource,
} from '../../utils/type';
import { BaseWidgetProps, WidgetHeightMessage } from '../../types/common.type';
import Loading from '../../Components/Loading/Loading';
import { ReactComponent as ErrorIcon } from '../../assets/icons/error-state.svg';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import { defaultPageNum, errorMsg } from '../../constants/common.constant';
import Layout from '../../Components/Common/Layout/Layout';
import { StockData } from '../../types/indices.type';

export interface ConsensusRecommendationWidgetProps extends BaseWidgetProps {
  token?: string;
  region?: Region;
  type?: Type;
  onCallback?: (message: RecommendationData | StockData) => void;
  onHeightChange?: (message: WidgetHeightMessage) => void;
}

const ConsensusRecommendationWidget = (
  props: ConsensusRecommendationWidgetProps,
) => {
  const { onHeightChange } = props;
  const theme = props.theme ?? ThemeType.Direct;
  const widgetRef = useRef<HTMLDivElement | null>(null);
  useResizeNotifier(
    widgetRef,
    WidgetNameSource.ConsensusRecommendation,
    onHeightChange,
  );
  const { width, isSmallScreen } = useContainerWidth(widgetRef);
  const msgItem = errorMsg.find(
    (obj) => obj.key === 'consensusRecommendations',
  );
  const [maxPageNum, setMaxPageNum] = useState(defaultPageNum);
  const RESULTS_PER_PAGE = 10;
  const [isLoading, setIsLoading] = useState(true);
  const onLoadingChangeRef = useRef(props.onLoadingChange);
  onLoadingChangeRef.current = props.onLoadingChange;
  useEffect(() => {
    onLoadingChangeRef.current?.(isLoading);
  }, [isLoading]);
  const [isEmptyData, setIsEmptyData] = useState(false);
  const [typeOptions, setTypeOptions] = useState(recommendationInitalMenu);
  const [type, setType] = useState<Type>(props.type || Type.All);
  const [region, setRegion] = useState<Region>(props.region || Region.AU);

  const [data, setData] = useState<RecommendationDataType[]>([]);
  const [total, setTotal] = useState(0);
  const [countMessage, setCountMessage] =
    useState<ConsensusRecommendationCount | null>(null);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationType>(
    paginate(page, RESULTS_PER_PAGE, total, maxPageNum),
  );

  useEffect(() => {
    if (width > 0) {
      setMaxPageNum(getMaxPageNum(width, false, false, false));
    }
  }, [width]);

  useEffect(() => {
    setPage(1);
    if (props.type) {
      setType(props.type);
    }
    if (props.region) {
      setRegion(props.region);
    }
  }, [props.type, props.region]);

  // Call API when page changes or indices change
  useEffect(() => {
    fetchRecommendationData();
  }, [page, props.token, region, type]);

  // Update pagination when total changes
  useEffect(() => {
    setPagination(paginate(page, RESULTS_PER_PAGE, total, maxPageNum));
  }, [total, page, maxPageNum]);

  const fetchRecommendationData = useCallback(async () => {
    setIsLoading(true);
    setIsEmptyData(false);
    setError(null);

    try {
      const res = await getRecommendationData(
        region,
        type,
        page,
        RESULTS_PER_PAGE,
        props.token || '',
      );
      setData(res.data);
      setTotal(res.total);
      setCountMessage(res.rcmdcount);
      setIsEmptyData(res.total === 0);
      if (res.total === 0) {
        props.onNoData?.({
          type: WidgetMessageType.NoData,
          source: 'ConsensusRecommendationWidget',
          data: null,
        });
      }
      handleCountCallback(res.rcmdcount);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch recommendation data');
      console.error('Error fetching recommendation data:', err);
      props.onError?.({
        message: err.message,
        type: err.name + '_' + err.code,
      });
      setIsEmptyData(true);
    } finally {
      setIsLoading(false);
    }
  }, [page, props.token, region, type]);

  const handleCountCallback = useCallback(
    (countMessage: ConsensusRecommendationCount) => {
      if (props.onCallback && countMessage) {
        let message = {
          data: countMessage,
          source: 'consensusrecommendation',
          type: WidgetMessageType.Count,
        };
        if ((window as any).ReactNativeWebView) {
          (window as any).ReactNativeWebView.postMessage(
            JSON.stringify(message),
            '*',
          );
        }
        window.parent.postMessage(JSON.stringify(message), '*');
        props.onCallback(message);
      }
    },
    [countMessage, props.onCallback],
  );

  const handleOnClick = (symbol: string) => {
    if (props.onCallback && symbol) {
      let message = {
        data: { symbol: symbol },
        source: 'consensusrecommendation',
        type: WidgetMessageType.OnStockClick,
      };
      if ((window as any).ReactNativeWebView) {
        (window as any).ReactNativeWebView.postMessage(
          JSON.stringify(message),
          '*',
        );
      }
      window.parent.postMessage(JSON.stringify(message), '*');
      props.onCallback(message);
    }
  };

  return (
    <Layout theme={theme}>
      <div ref={widgetRef} className="con-rec-container">
        <div className="con-rec">
          {/* <Header title="Consensus Recommendation" />
                <div className="con-rec-menu">
                    <div className="con-rec-dropdown">
                        <div className="con-rec-ctry-dropdown">
                            <Dropdown initialValue={selectedCountryIndex} options={countryMenu} onSelect={handleCountryDropdownSelect} />
                        </div>
                        {isSmallScreen ?
                            <div className="con-rec-rating-dropdown">
                                <Dropdown initialValue={selectedTypeIndex} options={typeOptions} onSelect={handleTypeDropdownSelect} />
                            </div> :
                            <div className="con-rec-rating-btn-group">
                                {
                                    typeOptions.map((item, index) =>
                                        <button key={index} className={`con-rec-rating-btn${selectedTypeIndex === index ? " selected" : ""}`}
                                            onClick={() => handleClick(index)}>
                                            {item.label}
                                        </button>)
                                }</div>
                        }
                    </div>
                </div> */}

          <div className="con-rec-table-container">
            {isLoading && !props.onLoadingChange ? (
              <Loading />
            ) : !isLoading && isEmptyData ? (
              <ErrorMessage msg={msgItem} />
            ) : (
              <RecommendationTable
                isLoading={isLoading}
                isSmallScreen={isSmallScreen}
                data={data}
                page={page}
                size={RESULTS_PER_PAGE}
                onStockClick={props.onCallback ? handleOnClick : undefined}
              />
            )}
          </div>
          <div className="con-rec-whitespace"></div>
          {!isLoading && !isEmptyData && (
            <Pagination
              pagination={pagination}
              onPageClick={(targetPage: number) => {
                setPage(targetPage);
              }}
              haveLastPage={true}
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ConsensusRecommendationWidget;
